import React, { useEffect, useState } from 'react';
import ProductSearchInputField from './ProductSearchInputField';
import { useTranslation } from 'react-i18next';
import './ProductSearch.scss';
import Spinner, { SpinnerSize } from '../common/Spinner/Spinner';
import OrganizationTypeahead from '../common/OrganizationTypeahead';
import { productApiClient } from '../../clients/ProductAPIClient';
import { IOptions } from '../../data/models/option';
import { logger } from '../../utils/Logger';

export interface Filters {
  [key: string]: any;
  label: string;
  includeSublabels: boolean;
  catalogNumber: string;
  releaseDate: string;
}

export interface SearchFields {
  [key: string]: any;
  keyword: string;
  artist: string;
  title: string;
}

const defaultFilters: Filters = {
  label: '',
  catalogNumber: '',
  includeSublabels: false,
  releaseDate: '',
};

const defaultSearchFields: SearchFields = {
  keyword: '',
  artist: '',
  title: '',
};

interface ProjectSearchProps {
  initialFilters: Filters;
  initialSearch: SearchFields;
  updateFilters: (filters: Filters, search: SearchFields) => void;
  resultCount: number;
}

export default function ProjectSearch(props: ProjectSearchProps) {
  const [filters, setFilters] = useState(props.initialFilters);
  const [searchFields, setSearchFields] = useState(props.initialSearch);
  const [resetTypeahead, setResetTypeahead] = useState(false);
  const [initialOrgs, setInitialOrgs] = useState<IOptions[]>([]);
  const [orgFilterLoading, setOrgFilterLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const labelIds = props.initialFilters.label.split(',').filter(Boolean);
    if (!labelIds.length) {
      setInitialOrgs([]);
      return;
    }

    setOrgFilterLoading(true);
    Promise.all(
      labelIds.map(async (labelId) => {
        try {
          const result = await productApiClient.getLabelById(labelId);
          const org = result.data;
          const orgId = org.adminOrgId ?? org.id ?? labelId;
          if (!orgId) {
            logger.warn(`No label found with id ${labelId}`);
            return null;
          }
          const parentName = org.parentOrgName
            ? ` <<${org.parentOrgName}>>`
            : '';
          return {
            value: orgId,
            label: `${org.name}${parentName}`,
          };
        } catch (e) {
          logger.warn(`No label found with id ${labelId}: ${e}`);
          return null;
        }
      })
    )
      .then((orgs) => {
        setInitialOrgs(orgs.filter((org): org is IOptions => !!org));
      })
      .finally(() => {
        setOrgFilterLoading(false);
      });
  }, [props.initialFilters.label]);

  const hasKeywordSet = (): boolean => {
    return props.initialSearch.keyword !== defaultSearchFields.keyword;
  };

  const hasAdvancedSearchSet = (): boolean => {
    return (
      Object.keys(props.initialFilters).some(
        (key) => props.initialFilters[key] !== defaultFilters[key]
      ) ||
      Object.keys(props.initialSearch).some(
        (key) =>
          props.initialSearch[key] !== defaultSearchFields[key] &&
          key !== 'keyword'
      )
    );
  };
  const [isExpanded, setIsExpanded] = useState(hasAdvancedSearchSet());
  const [showResultCount, setShowResultCount] = useState(
    hasAdvancedSearchSet() || hasKeywordSet()
  );

  const search = (event: React.FormEvent) => {
    event.preventDefault();
    props.updateFilters(filters, searchFields);
    setShowResultCount(true);
  };

  const toggleAdvancedSearch = () => {
    setIsExpanded(!isExpanded);
  };

  const resetAdvancedSearch = () => {
    setFilters(defaultFilters);
    setSearchFields({ ...searchFields, artist: '', name: '' });
    setResetTypeahead(true);
  };

  const clearSearch = () => {
    setFilters(defaultFilters);
    setResetTypeahead(true);
    setSearchFields(defaultSearchFields);
    setShowResultCount(false);
    props.updateFilters(defaultFilters, defaultSearchFields);
  };

  return (
    <div className="project-search">
      <form onSubmit={search}>
        <div className="input-group search-bar">
          <input
            id="keywordSearch"
            name="keywordSearch"
            value={searchFields.keyword}
            className="form-control"
            onChange={(u) =>
              setSearchFields({ ...searchFields, keyword: u.target.value })
            }
            maxLength={200}
          />
          <i className="icon search-icon fas fa-search"></i>
          <button
            type="button"
            className={`advanced-toggle-btn btn btn-secondary ${isExpanded ? 'expanded' : 'collapsed'}`}
            onClick={toggleAdvancedSearch}
          >
            <i className="icon fas fa-sliders-h fa-lg"></i>
          </button>
        </div>
      </form>
      {isExpanded ? (
        <div className="advanced-search-container">
          <form id="advancedSearchForm" onSubmit={search}>
            <div className="row">
              <ProductSearchInputField
                labelValue={t('product.editProduct.title')}
                inputId="titleSearch"
                inputValue={searchFields.title}
                onInputChange={(t) =>
                  setSearchFields({ ...searchFields, title: t })
                }
              />
              <ProductSearchInputField
                labelValue={t('product.editProduct.displayArtist')}
                inputId="artistSearch"
                inputValue={searchFields.artist}
                onInputChange={(a) =>
                  setSearchFields({ ...searchFields, artist: a })
                }
              />
              <ProductSearchInputField
                labelValue={t('product.productSearch.catalogNumber')}
                inputId="catalogSearch"
                inputValue={filters.catalogNumber}
                onInputChange={(c) =>
                  setFilters({ ...filters, catalogNumber: c })
                }
              />
            </div>
            <div className="row">
              <div className="col-12 col-sm-4 form-group">
                <label
                  className="modal-label form-label"
                  htmlFor="organizationTypeahead"
                >
                  {t('product.editProduct.labelname')}
                </label>
                {orgFilterLoading ? (
                  <div>
                    <Spinner
                      className="input-loading-spinner"
                      size={SpinnerSize.Small}
                      loadingText={t('common.generic.loading')}
                    />
                  </div>
                ) : (
                  <OrganizationTypeahead
                    resetTypeahead={resetTypeahead}
                    setResetTypeahead={setResetTypeahead}
                    onSelectOrganizations={(orgs: IOptions[]) => {
                      setFilters({
                        ...filters,
                        label: orgs.map((org) => org.value).join(','),
                      });
                    }}
                    initialOrgs={initialOrgs}
                  />
                )}
              </div>
              <div className="col-12 col-sm-8 advanced-search-buttons-container">
                <button type="submit" className="btn btn-primary">
                  {t('product.productSearch.search')}
                </button>
                <button
                  type="button"
                  id="resetAdvancedSearchButton"
                  className="btn btn-secondary"
                  onClick={() => resetAdvancedSearch()}
                >
                  {t('product.productSearch.reset')}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}

      {showResultCount ? (
        <div className="search-results">
          <div className="result-count-container">
            <span className="result-count-txt">
              {t('product.productSearch.resultCount', {
                resultCount: props.resultCount.toString(),
              })}
            </span>
            <button
              type="button"
              className="result-clear-btn icon-btn"
              onClick={() => clearSearch()}
            >
              <i className="icon fas fa-times"></i>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
